import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import Loading from '../../../../components/Loading';
import ConfirmationModal from '../../../../components/Modals/ConfirmationModal';
import ReactSelect from '../../../../components/ReactSelect';
import { BULK_FREQUENCY_OPTIONS } from '../../create/utils';
import SettingsCard from '../SettingsCard';
import SettingsField from '../SettingsField';
import { bulkUpdateFrequency } from '../query';

const prefix = 'ai_tracking.projects.settings.frequency';

/**
 * One frequency for every prompt in the project, active and inactive alike.
 *
 * The select starts empty on purpose: a project's prompts can each have a different frequency,
 * so there is no single "current" value to show, and pre-selecting one would invite an
 * accidental overwrite. Applying is a bulk write across the whole project, so it always goes
 * through a confirmation first.
 */
class Frequency extends Component {
    constructor() {
        super();

        this.onSelect = this.onSelect.bind(this);
        this.onOpenConfirm = this.onOpenConfirm.bind(this);
        this.onCloseConfirm = this.onCloseConfirm.bind(this);
        this.onApply = this.onApply.bind(this);

        this.state = {
            frequency: null,
            isConfirmOpen: false,
            saving: false,
            error: '',
        };
    }

    onSelect(frequency) {
        this.setState({ frequency, error: '' });
    }

    onOpenConfirm() {
        this.setState({ isConfirmOpen: true });
    }

    onCloseConfirm() {
        this.setState({ isConfirmOpen: false });
    }

    async onApply() {
        try {
            this.setState({ saving: true, error: '' });
            const res = await bulkUpdateFrequency({
                frequency: this.state.frequency,
                project: Number(this.props.projectId),
            });
            const data = await res.json();

            if (res.status !== 200) {
                this.setState({
                    error: data?.detail ?? this.props.t('global.something_went_wrong'),
                });
                return;
            }

            this.setState({ frequency: null });
            if (this.props.onApplied) this.props.onApplied(data.updated_count);
        } catch (err) {
            this.setState({
                error: err?.message ?? this.props.t('global.something_went_wrong'),
            });
        } finally {
            this.setState({ saving: false, isConfirmOpen: false });
        }
    }

    render() {
        const { t } = this.props;
        const { frequency, saving, error, isConfirmOpen } = this.state;
        const options = BULK_FREQUENCY_OPTIONS(t);

        const footer = (
            <button
                type="button"
                className="button button-filled button-primary width-sm btn d-flex gap-5 align-items-center justify-content-center"
                disabled={!frequency || saving}
                onClick={this.onOpenConfirm}
            >
                {saving && <Loading onlyPreloader />}
                {t(`${prefix}.apply`)}
            </button>
        );

        return (
            <>
                <SettingsCard
                    title={t(`${prefix}.header`)}
                    subtitle={t(`${prefix}.subtitle`)}
                    footer={footer}
                    footerNote={t(`${prefix}.note`)}
                >
                    <SettingsField title={t(`${prefix}.label`)} hint={t(`${prefix}.hint`)}>
                        <ReactSelect
                            options={options}
                            value={options.find((x) => x.value === frequency) ?? null}
                            placeholder={t('ai_tracking.add_keyword.frequency')}
                            onChange={this.onSelect}
                            isSearchable={false}
                            blurInputOnSelect={true}
                            classname="mx-0"
                            disabled={saving}
                        />
                    </SettingsField>
                    {!!error && (
                        <div className="competitors-error-message my-10">
                            <img src="/static/img/alert-icon.svg" alt="icon" />
                            {error}
                        </div>
                    )}
                </SettingsCard>
                <ConfirmationModal
                    isOpen={isConfirmOpen}
                    message={t(`${prefix}.confirm`)}
                    onClose={this.onCloseConfirm}
                    onSubmit={this.onApply}
                    cancleBtn={t('ai_tracking.global.no')}
                    buttonName={t('ai_tracking.global.yes')}
                    loading={saving}
                />
            </>
        );
    }
}

export default withTranslation()(Frequency);
